import axios from "axios";
import {store} from "../store";
import * as MutationTypes from "../store/mutation-types";
import appConfig from "../appconfig";
import * as domainsUtils from "../domains2/libraries/Utils";
import { flowSetting } from "../libraries/utils";
import * as mutationTypes from "../store/mutation-types";

const tenantId = isFinite(appConfig.tenantId) ? appConfig.tenantId : 0;
const axiosConfig = {
	baseURL: appConfig.getBaseApiFeathersUrl(),
	timeout: 300000,
	headers: {
		"Content-Type": "application/json",
		"X-Browser-Name": appConfig.browserInfo.name,
		"X-Browser-Version": appConfig.browserInfo.version,
		"X-Client-TraceId": appConfig.clientTraceId(),
		"X-Client-Platform": appConfig.platform.platformId,
		"X-Client-PlatformVersion": appConfig.platform.platformVersion,
		"X-Tenant-Id": tenantId
	}
};

const cdnCachedRoutes = [
	"/pickup-date-time-rules",
	"/countries-provider",
	"/shops",
	"/upsale-rules/results/upsell",
	"/localization",
	"/target-groups",
	"/intusr/shops",
	"/viking-api/holidays",
	"/intusr/targetGroups"
];

const addInterceptors = function (axios, apiPostFix) {
	axios.interceptors.request.use(config => {
		if(apiPostFix && !flowSetting.isFlow("admin") && config.method === "get"
			&& store && store.getters && store.getters.featureToggles && store.getters.featureToggles.useApiCdnCaching
			&& config.url && cdnCachedRoutes.some(route => `${(config.url.startsWith("/") ? "" : "/")}${config.url}`.startsWith(route))) {

			return {
				...config,
				baseURL: getBaseUrl(apiPostFix)
			};
		}

		return config;
	});

	axios.interceptors.request.use(async function (config) {
		config.headers["X-Request-TraceId"] = domainsUtils.generateTraceId("RT");
		config.headers["X-App-Version"] = (await appConfig.getAppInfo()).version;
		config.headers["X-App-Channel"] = appConfig.channelAlias();
		return config;
	}, function (error) {
		if(!store) {
			return Promise.reject(error);
		}

		store.dispatch(MutationTypes.ADD_GLOBAL_ERROR, error);
		return Promise.reject(error);
	});

	axios.interceptors.request.use(function (config) {
		if (localStorage.getItem("overrideHeaders") !== null) {
			// Skip setting these headers and use default
			Object.assign(config.headers, JSON.parse(localStorage.getItem("overrideHeaders")));
		} else {
			const isAdmin = localStorage.getItem("admin-user");
			if (isAdmin) {
				config.headers["cache-control"] = "no-cache";
			} else {
				config.headers["cache-control"] = "max-age=60";
			}
		}
		return config;
	}, function (error) {
		if(!store) {
			return Promise.reject(error);
		}

		store.dispatch(MutationTypes.ADD_GLOBAL_ERROR, error);
		return Promise.reject(error);
	});

	// Add a response interceptor
	axios.interceptors.response.use(function (response) {
		// if we have refreshTokenCounter in the normal context, we will remove it
		// since this means that jwt token is valid
		if(localStorage.getItem("refreshTokenCounter") && store && store.getters.applicationUserIdpToken) {
			localStorage.removeItem("refreshTokenCounter");
		}
		// Do something with response data
		return response;
	}, async function (error) {
		if(!store) {
			return Promise.reject(error);
		}

		const status = error.response ? error.response.status : null;
		const errorMessage = error.response && error.response.data ? error.response.data.message : null;

		// if request has Authorizationidp and code returned is 401 and message is user-logged-out - log out.
		// It is needed for supporting the scenario when user changes password and logging out on all his devices
		// TODO: In future we should implement the way for invalidation the refresh token from the idp service side
		if (status === 401 && error.config.headers["Authorizationidp"] && errorMessage === "user-logged-out") {
			store.commit(mutationTypes.REMOVE_USER_IDP_TOKEN);
			await store.dispatch(mutationTypes.PERSON_DATA_CLEAR);
			await store.dispatch(mutationTypes.GATHER_EVALUATION_TAG_DATA);
			await store.dispatch(mutationTypes.REMOVE_USER_DATA);
			window.location.href = "/";
			return;
		}

		// if request has Authorizationidp and code returned is 401 - try to refresh the access token and resend the request
		// solution from https://stackoverflow.com/questions/51563821/axios-interceptors-retry-original-request-and-access-original-promise
		if ((status === 401 && error.config.headers["Authorizationidp"]) || (store.getters.isSubscribeFlow && errorMessage === "user-not-found")) {
			// this will run if there was previous not successful attempt to generate refresh token or the token is invalid for any reason
			// or if user was removed from the db in the meantime but valid token remains in the client state
			// the known case for this is when idp service and the app backend are not timely synced
			// localstorage will be cleared and user redirected to the login page
			const refreshTokenCounter = localStorage.getItem("refreshTokenCounter");
			if(
				(refreshTokenCounter && parseInt(refreshTokenCounter) >= 10)
				|| (store.getters.isSubscribeFlow && errorMessage === "user-not-found")
			) {
				store.commit(MutationTypes.REMOVE_USER_IDP_TOKEN);
				await store.dispatch(MutationTypes.PERSON_DATA_CLEAR);
				await store.dispatch(MutationTypes.REMOVE_USER_DATA);
				await store.dispatch(MutationTypes.GATHER_EVALUATION_TAG_DATA);
				const { router } = await import("../router");
				router.push("/check-email");
			}
			if (appConfig.channelAlias() === "kiosk") {
				store.commit(MutationTypes.REMOVE_USER_IDP_TOKEN);
				await store.dispatch(MutationTypes.PERSON_DATA_CLEAR);
				await store.dispatch(MutationTypes.REMOVE_USER_DATA);
				window.location.href = "/";
				return;
			}

			return store.dispatch(MutationTypes.RESET_USER_ACCESS_TOKEN_USING_REFRESH_TOKEN)
				.then(() => {
					error.config.headers["Authorizationidp"] = store.getters.applicationUserIdpToken;
					error.config.baseURL = undefined;
					// refresh token initiated, let's count up to 10 failing responses with 401
					// if we exceed that number, we are probably in the infinite loop, which means
					// that token cannot be regenerated and we'll ask users to login again
					// know case - both jwt token and refresh token expired
					if(localStorage.getItem("refreshTokenCounter")) {
						localStorage.setItem("refreshTokenCounter", `${parseInt(localStorage.getItem("refreshTokenCounter")) + 1}`);
					} else {
						localStorage.setItem("refreshTokenCounter", "1");
					}
					return axios.request(error.config);
				});
		}

		const configUrl = error.config && error.config.url || error.response && error.response.config && error.response.config.url;
		if (!configUrl || !configUrl.includes("/clientlogging"))
		{
			// do not log error about not being able to log because
			// it causes infinite loop :)
			const errorObj = error.response && error.response.data ? error.response.data : error;
			if (store) {
				store.dispatch(MutationTypes.ADD_GLOBAL_ERROR, errorObj);
			}
		}

		// Do something with response error
		return Promise.reject(error);
	});
};

function getBaseUrl(postFix = ""){
	if(appConfig.loadedEnvironment() === "a-env") return appConfig.getBaseUrl() + postFix;

	return `${appConfig.mobile.isMobileApp() ? appConfig.getBaseUrl(): window.location.origin}${postFix}`;
}

const axiosConfigForNest = {
	...axiosConfig,
	baseURL: appConfig.getBaseApiNestUrl()
};

const BifrostBackendApi = axios.create(axiosConfig);
addInterceptors(BifrostBackendApi, "/api/");

const BifrostBackendNestApi = axios.create(axiosConfigForNest);
addInterceptors(BifrostBackendNestApi, "/api/v2/");

const BifrostBackendViaCdnApi = axios.create({ ...axiosConfig,
	baseURL: getBaseUrl("/api/")
});
addInterceptors(BifrostBackendViaCdnApi);

const BifrostBackendViaCdnApiV2 = axios.create({ ...axiosConfig,
	baseURL: getBaseUrl("/api/v2/")
});
addInterceptors(BifrostBackendViaCdnApiV2);

export { BifrostBackendApi, BifrostBackendNestApi, BifrostBackendViaCdnApi, BifrostBackendViaCdnApiV2 };
